import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { verifyTOTPCode, getDeviceInfo, generateDeviceId } from '@/lib/totp';
import { showError } from '@/lib/swal';
import { Shield, Mail } from 'lucide-react';

export default function TwoFactorVerifyPage() {
  const { currentUser, logout, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as any)?.returnTo || '/dashboard';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackupMethod, setUseBackupMethod] = useState<'app' | 'backup' | 'email'>('app');
  const [emailSent, setEmailSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // If no user is logged in, redirect to login
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Get user email
    setUserEmail(currentUser.email || '');

    // Check if user actually needs 2FA
    const check2FAStatus = async () => {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        navigate(returnTo);
        return;
      }

      const userData = userDoc.data();
      
      // If 2FA is not enabled, redirect to intended destination
      if (!userData.twoFactorEnabled) {
        navigate(returnTo);
        return;
      }

      // Check if device is already trusted
      const deviceId = generateDeviceId();
      const trustedDevices = userData.trustedDevices || [];
      const isTrusted = trustedDevices.some((d: any) => d.id === deviceId);

      if (isTrusted) {
        // Device is trusted, go to intended destination
        navigate(returnTo);
      }
    };

    check2FAStatus();
  }, [currentUser, navigate, returnTo]);

  const handleVerify = async () => {
    if (!code || !currentUser) {
      showError('Please enter the verification code');
      return;
    }

    setLoading(true);

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        showError('User not found');
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      
      // Debug logging
      console.log('2FA Verification Debug:', {
        has2FAEnabled: userData.twoFactorEnabled,
        hasSecret: !!userData.twoFactorSecret,
        hasBackupCodes: !!userData.twoFactorBackupCodes,
        backupCodesCount: userData.twoFactorBackupCodes?.length,
        codeEntered: code,
        useBackupMethod,
      });

      if (!userData.twoFactorEnabled) {
        showError('Two-factor authentication is not enabled for this account');
        navigate('/dashboard');
        setLoading(false);
        return;
      }

      let isValid = false;
      const isBackupCode = useBackupMethod === 'backup';

      if (isBackupCode) {
        // Check backup code
        isValid = userData.twoFactorBackupCodes?.includes(code) || false;
        console.log('Backup code check:', { code, isValid, availableCodes: userData.twoFactorBackupCodes });
        
        if (isValid) {
          // Remove used backup code
          await updateDoc(doc(db, 'users', currentUser.uid), {
            twoFactorBackupCodes: arrayRemove(code),
          });
        }
      } else {
        // Verify TOTP code
        if (!userData.twoFactorSecret) {
          showError('2FA secret not found. Please contact support.');
          setLoading(false);
          return;
        }
        isValid = verifyTOTPCode(userData.twoFactorSecret, code);
        console.log('TOTP verification:', { code, isValid, hasSecret: !!userData.twoFactorSecret });
      }

      if (isValid) {
        console.log('✅ Code verified successfully');
        
        // Add device as trusted
        const deviceInfo = getDeviceInfo();
        const deviceId = generateDeviceId();
        const newDevice = {
          id: deviceId,
          name: deviceInfo.name,
          userAgent: deviceInfo.userAgent,
          lastUsed: new Date(),
          createdAt: new Date(),
        };

        console.log('Adding trusted device:', newDevice);
        console.log('Device ID being added:', deviceId);

        await updateDoc(doc(db, 'users', currentUser.uid), {
          trustedDevices: arrayUnion(newDevice),
        });

        console.log('Device added to Firestore, refreshing user profile...');
        await refreshUserProfile();
        
        // Wait a moment to ensure context is updated
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('Profile refreshed, verifying device is in list...');
        const updatedUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const updatedData = updatedUserDoc.data();
        const deviceNowTrusted = updatedData?.trustedDevices?.some((d: any) => d.id === deviceId);
        console.log('Device now trusted?', deviceNowTrusted);
        console.log('All trusted devices:', updatedData?.trustedDevices?.map((d: any) => d.id));
        
        // Redirect to intended destination or dashboard
        const redirectTo = (location.state as any)?.returnTo || (location.state as any)?.from?.pathname || '/dashboard';
        console.log('Redirecting to:', redirectTo);
        navigate(redirectTo);
      } else {
        console.log('❌ Code verification failed');
        showError(
          useBackupMethod === 'backup' 
            ? 'Invalid backup code. Please try again.' 
            : 'Invalid verification code. Please try again.'
        );
        setCode('');
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      showError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEmailCode = async () => {
    setLoading(true);
    try {
      // TODO: Implement email code functionality
      showError('Email backup code functionality is not yet implemented. Please use your authenticator app or backup codes.');
      setEmailSent(true);
      setUseBackupMethod('email');
    } catch (error) {
      showError('Failed to send email code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getTitle = () => {
    if (useBackupMethod === 'backup') return 'Enter Backup Code';
    if (useBackupMethod === 'email') return 'Enter Email Code';
    return 'Two-Factor Authentication Required';
  };

  const getDescription = () => {
    if (useBackupMethod === 'backup') {
      return 'Enter one of your backup codes to access your account';
    }
    if (useBackupMethod === 'email') {
      return `We've sent a verification code to ${userEmail}`;
    }
    return 'Enter the 6-digit code from your authenticator app';
  };

  const getPlaceholder = () => {
    if (useBackupMethod === 'backup') return 'XXXX-XXXX';
    return '000000';
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="text-center mb-4">
              <img 
                src="/logo.png" 
                alt="Dining Steward Logo" 
                className="h-10 w-10 mx-auto mb-2"
              />
              <span className="text-lg font-semibold text-navy">Dining Steward</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>{getTitle()}</CardTitle>
            </div>
            <CardDescription>{getDescription()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Verification Code Input */}
            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                placeholder={getPlaceholder()}
                value={code}
                onChange={(e) => {
                  if (useBackupMethod === 'backup') {
                    // Allow alphanumeric and dash for backup codes
                    setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9));
                  } else {
                    // Only digits for TOTP
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  }
                }}
                className="text-center text-2xl tracking-widest font-mono"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && code.length >= 6) {
                    handleVerify();
                  }
                }}
              />
            </div>

            {/* Verify Button */}
            <Button 
              onClick={handleVerify} 
              className="w-full"
              disabled={loading || code.length < 6}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </Button>

            {/* Alternative Methods */}
            {useBackupMethod === 'app' && (
              <div className="space-y-2 pt-4 border-t">
                <p className="text-sm text-center text-muted-foreground mb-3">
                  Having trouble?
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setUseBackupMethod('backup');
                      setCode('');
                    }}
                    className="w-full"
                  >
                    Use Backup Code
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRequestEmailCode}
                    disabled={loading || emailSent}
                    className="w-full"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {emailSent ? 'Email Sent' : 'Send Code via Email'}
                  </Button>
                </div>
              </div>
            )}

            {useBackupMethod !== 'app' && (
              <Button
                variant="ghost"
                onClick={() => {
                  setUseBackupMethod('app');
                  setCode('');
                }}
                className="w-full"
              >
                ← Back to Authenticator App
              </Button>
            )}

            {/* Help Text */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
              {useBackupMethod === 'app' && (
                <p>Open your authenticator app and enter the 6-digit code for Dining Steward.</p>
              )}
              {useBackupMethod === 'backup' && (
                <p>Enter one of the backup codes you saved when setting up 2FA. Format: XXXX-XXXX</p>
              )}
              {useBackupMethod === 'email' && (
                <p>Check your email for the verification code. It may take a few minutes to arrive.</p>
              )}
            </div>

            {/* Logout Option */}
            <div className="pt-4 border-t">
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full text-muted-foreground"
              >
                Log out and try a different account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

